import React, { useState } from 'react';
import { useFinance } from '../contexts/FinanceContext';
import { Edit, Trash2, CheckCircle, Circle, Calendar, Tag, FileText, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';
import EditPaymentModal from './EditPaymentModal';

const PaymentsList = () => {
  const { financeData, togglePayment, deletePayment } = useFinance();
  const [editingPayment, setEditingPayment] = useState(null);
  const [sortByInstallments, setSortByInstallments] = useState('none');
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);

  const getMonthName = (monthNumber) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber - 1] || 'Desconocido';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getCategoryColor = (category) => {
    const colors = {
      suscripciones: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      casa: 'bg-blue-100 text-blue-800 border-blue-200',
      plazos: 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[category] || colors.suscripciones;
  };

  const isLegacyPayment = (payment) => {
    return !payment.useExactDates && !payment.startDate && !payment.endDate;
  };

  const handleDelete = (id) => {
    if (confirm('¿Estás seguro de que quieres eliminar este pago?')) {
      deletePayment(id);
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
  };

  const closeEditModal = () => {
    setEditingPayment(null);
  };

  const handleSortByInstallments = () => {
    if (sortByInstallments === 'none') {
      setSortByInstallments('asc');
    } else if (sortByInstallments === 'asc') {
      setSortByInstallments('desc');
    } else {
      setSortByInstallments('none');
    }
  };

  const getSortButtonIcon = () => {
    switch (sortByInstallments) {
      case 'asc':
        return <ArrowUp className="w-4 h-4" />;
      case 'desc':
        return <ArrowDown className="w-4 h-4" />;
      default:
        return <ArrowUpDown className="w-4 h-4" />;
    }
  };

  const toggleExpand = (id) => {
    setExpandedPaymentId(expandedPaymentId === id ? null : id);
  };

  if (financeData.payments.length === 0) {
    return (
      <div className="card mb-6 md:mb-8">
        <div className="text-center py-8 md:py-12">
          <div className="text-5xl md:text-6xl mb-4">📋</div>
          <h2 className="section-title justify-center mb-2">Pagos Mensuales</h2>
          <p className="text-gray-600">No hay pagos registrados. ¡Agrega tu primer pago!</p>
        </div>
      </div>
    );
  }

  const sortedPayments = [...financeData.payments].sort((a, b) => {
    if (a.paid !== b.paid) {
      return a.paid - b.paid;
    }

    if (sortByInstallments === 'asc') {
      return (a.installments || 0) - (b.installments || 0);
    } else if (sortByInstallments === 'desc') {
      return (b.installments || 0) - (a.installments || 0);
    }

    return (a.paymentDay || 0) - (b.paymentDay || 0);
  });

  return (
    <>
      <div className="card mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
          <h2 className="section-title">📋 Pagos Mensuales</h2>

          <button
            onClick={handleSortByInstallments}
            className={`btn btn-sm ${sortByInstallments === 'none'
                ? 'btn-secondary'
                : 'btn-primary'
              }`}
          >
            {getSortButtonIcon()}
            <span className="hidden sm:inline">
              {sortByInstallments === 'none' ? 'Ordenar' :
                sortByInstallments === 'asc' ? 'Menos plazos' : 'Más plazos'}
            </span>
            <span className="sm:hidden">Ordenar</span>
          </button>
        </div>

        <div className="space-y-3 md:space-y-4">
          {sortedPayments.map((payment) => {
            const isExpanded = expandedPaymentId === payment.id;

            return (
              <div
                key={payment.id}
                className={`payment-card ${payment.paid
                    ? 'bg-green-50 border-green-200 opacity-80'
                    : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
              >
                {/* Main Row - Always visible */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className={`text-base md:text-lg font-bold truncate ${payment.paid ? 'text-green-700 line-through' : 'text-gray-800'
                        }`}>
                        {payment.name}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(payment.category)}`}>
                        {payment.category}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-xl md:text-2xl font-bold text-blue-600">
                        {formatCurrency(payment.amount)}
                      </span>
                      <span className="text-sm text-gray-500">
                        Día {payment.paymentDay || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => togglePayment(payment.id)}
                      className={`btn btn-sm ${payment.paid ? 'btn-success' : 'btn-primary'
                        }`}
                      aria-label={payment.paid ? 'Marcar pendiente' : 'Marcar pagado'}
                    >
                      {payment.paid ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Circle className="w-5 h-5" />
                      )}
                      <span className="hidden sm:inline">
                        {payment.paid ? 'Pagado' : 'Pagar'}
                      </span>
                    </button>

                    <button
                      onClick={() => toggleExpand(payment.id)}
                      className="btn btn-sm btn-secondary"
                      aria-label="Ver detalles"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded Details */}
                <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                  }`}>
                  {isLegacyPayment(payment) && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                      <div className="flex items-start gap-2">
                        <span className="text-orange-600 text-sm">⚠️</span>
                        <p className="text-sm text-orange-800">
                          <strong>Datos antiguos:</strong> Edita para usar fechas exactas.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm truncate">
                        Día {payment.paymentDay || 'N/A'}
                      </span>
                    </div>

                    {payment.useExactDates && payment.startDate && (
                      <div className="text-sm text-gray-600 truncate">
                        <strong>Inicio:</strong> {new Date(payment.startDate).toLocaleDateString('es-ES')}
                      </div>
                    )}

                    {payment.useExactDates && payment.endDate && (
                      <div className="text-sm text-gray-600 truncate">
                        <strong>Fin:</strong> {new Date(payment.endDate).toLocaleDateString('es-ES')}
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-gray-600">
                      <Tag className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">
                        {payment.paid ? '✅ Pagado' : '⏳ Pendiente'}
                      </span>
                    </div>

                    {payment.installments > 0 && (
                      <div className="text-sm text-gray-600 col-span-2">
                        <strong>Cuotas:</strong> {payment.installments} restantes
                      </div>
                    )}

                    {payment.canPayOff && payment.payoffAmount > 0 && (
                      <div className="text-sm text-gray-600 col-span-2">
                        <strong>Pago único:</strong> {formatCurrency(payment.payoffAmount)}
                      </div>
                    )}
                  </div>

                  {payment.observations && (
                    <div className="flex items-start gap-2 text-gray-600 mb-4 p-3 bg-gray-50 rounded-lg">
                      <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p className="text-sm">{payment.observations}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => handleEdit(payment)}
                      className="btn btn-sm btn-secondary"
                    >
                      <Edit className="w-4 h-4" />
                      Editar
                    </button>

                    <button
                      onClick={() => handleDelete(payment.id)}
                      className="btn btn-sm btn-danger"
                    >
                      <Trash2 className="w-4 h-4" />
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          onClose={closeEditModal}
        />
      )}
    </>
  );
};

export default PaymentsList;